import { HelpDeskSectionTitle } from './HelpDeskSectionTitle';
import { SettingsIcon } from '@src/components/icons/SettingsIcon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Stack, Hint, showDialog } from '@src/components';
import { pushDialog } from '@src/components/Dialog/Dialog';
import { TrashIcon } from '@src/components/icons/TrashIcon';

type CardActionTarget = { id: number; active: boolean; is_participant: boolean; is_admin: boolean; claimed_at: string | null; box_active: boolean | null; box_archived: boolean | null };
export function HelpDeskCardActions({ card, onChanged, onDenied }: { card: CardActionTarget; onChanged: () => void; onDenied: () => void }) {
  const { i18n } = useTranslation();
  const ru = i18n.language.startsWith('ru');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const available = card.active && card.box_active && !card.box_archived;
  async function deleteCard() {
    setBusy(true); setStatus('');
    try {
      const response = await fetch(`/api/admin/help-desk/cards/${card.id}`, { method: 'DELETE', credentials: 'same-origin' });
      if ([401, 403].includes(response.status)) { onDenied(); throw new Error(ru ? 'Доступ запрещён.' : 'Access denied.'); }
      if (!response.ok) {
        const error = await response.text();
        if (error.includes('CANNOT_DELETE_CARD_AFTER_DRAW_TOO_FEW_USERS')) {
          throw new Error(ru ? 'После жеребьёвки осталось слишком мало участников для удаления.' : 'Too few participants remain to remove this card after the draw.');
        }
        throw new Error(ru ? 'Не удалось выполнить действие. Попробуйте ещё раз.' : 'Action failed. Please try again.');
      }
      onChanged();
      setStatus(ru ? 'Участие в игре удалено.' : 'Participation removed.');
    }
    finally { setBusy(false); }
  }
  function confirmDeletion(error?: string) {
    const open = error ? showDialog : pushDialog;
    open({
      title: ru ? `Удалить карточку #${card.id}?` : `Delete card #${card.id}?`,
      testId: 'help-desk-card-delete-confirm',
      closeLabel: ru ? 'Закрыть' : 'Close',
      cancel: ru ? 'Отмена' : 'Cancel',
      submit: ru ? 'Подтвердить удаление' : 'Confirm removal',
      submitVariant: 'danger',
      children: <Stack gap={3}>
        <p>{ru ? 'Удалить участие в игре? Если жеребьёвка проведена, подопечные будут переназначены, а затронутые участники получат стандартные уведомления.' : 'Remove participation? If the draw is complete, giftees will be reassigned and affected participants will receive the standard notifications.'}</p>
        {card.is_admin && <p>{ru ? 'Права организатора сохранятся.' : 'Organizer access will be preserved.'}</p>}
        {error && <Hint>{error}</Hint>}
      </Stack>,
      onSubmit: async () => {
        try { await deleteCard(); }
        catch (error) {
          confirmDeletion(error instanceof Error ? error.message : String(error));
          throw error;
        }
      },
    });
  }
  return <section className="help-desk__card-actions" aria-label={ru ? 'Действия с карточкой' : 'Card actions'}>
    <HelpDeskSectionTitle icon={<SettingsIcon />}>{ru ? 'Действия' : 'Actions'}</HelpDeskSectionTitle>
    <Button variant="danger" icon={<TrashIcon />} disabled={!available || !card.is_participant || busy} onClick={() => confirmDeletion()}>{ru ? 'Удалить карточку' : 'Delete card'}</Button>
    {status && <p role="status">{status}</p>}
  </section>;
}
